'use client';
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, getTestId } from '../../utils';
import { useMediaQuery } from '../../hooks/use-media-query';
import { BottomSheet } from '../bottom-sheet/BottomSheet';
import { Button } from '../Button';

/** Tailwind `md` breakpoint (768 px): from here up the panel sits beside the content. */
const DESKTOP_QUERY = '(min-width: 48rem)';

const sidePanelVariants = cva(
  'flex min-h-0 max-h-full max-w-[50%] shrink-0 flex-col self-stretch overflow-hidden rounded-xl border border-border-base bg-background-input shadow-sm',
  {
    variants: {
      width: {
        sm: 'w-[360px]',
        md: 'w-[480px]',
        lg: 'w-[640px]'
      }
    },
    defaultVariants: {
      width: 'md'
    }
  }
);

type SidePanelVariantProps = VariantProps<typeof sidePanelVariants>;

export interface SidePanelProps extends SidePanelVariantProps {
  /** Whether the panel is shown */
  open: boolean;
  /** Called by the close button, Esc, and (narrow screens) a click on the backdrop */
  onClose: () => void;
  /** Heading of the panel; names the panel (`aria-labelledby`) unless `aria-label` is given */
  title: React.ReactNode;
  /** Secondary line under the title; describes the panel */
  subtitle?: React.ReactNode;
  /** Accessible label of the close button */
  closeLabel: string;
  /** Scrollable body */
  children: React.ReactNode;
  /** Actions pinned below the body */
  footer?: React.ReactNode;
  /** Accessible name overriding the title */
  'aria-label'?: string;
  /** Additional classes for the panel surface */
  className?: string;
  testId?: string;
  id?: string;
}

interface FocusReturnProps {
  panelRef: React.RefObject<HTMLElement | null>;
  onRestore: () => void;
}

/**
 * Returns focus when the panel unmounts with focus inside. Rendered inside the panel: a child's
 * layout cleanup runs before React removes the panel element, while focus is still in it.
 */
function FocusReturn({ panelRef, onRestore }: FocusReturnProps) {
  React.useLayoutEffect(
    () => () => {
      if (panelRef.current?.contains(document.activeElement)) onRestore();
    },
    [panelRef, onRestore]
  );
  return null;
}

/**
 * Panel beside a list: non-modal on `md` and up, an overlay sheet on narrow screens.
 * Place it in a height-bounded flex row next to the list it belongs to.
 */
export const SidePanel = React.forwardRef<HTMLElement, SidePanelProps>(
  (
    {
      open,
      onClose,
      title,
      subtitle,
      closeLabel,
      children,
      footer,
      width,
      'aria-label': ariaLabel,
      className,
      testId,
      id
    },
    ref
  ) => {
    const isDesktop = useMediaQuery(DESKTOP_QUERY);
    const titleId = React.useId();
    const subtitleId = React.useId();
    const panelRef = React.useRef<HTMLElement | null>(null);
    const returnFocusRef = React.useRef<HTMLElement | null>(null);
    const openerRef = React.useRef<HTMLElement | null>(null);

    const setPanelRef = React.useCallback(
      (node: HTMLElement | null) => {
        // Kept after detach: FocusReturn's cleanup runs after the ref is cleared.
        if (node) panelRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );

    // Remembers the last focused element outside the panel, so closing from inside returns there.
    React.useEffect(() => {
      if (!open || !isDesktop) return;
      const isReturnTarget = (target: EventTarget | null): target is HTMLElement =>
        target instanceof HTMLElement &&
        target !== document.body &&
        !panelRef.current?.contains(target) &&
        target.closest('[role="dialog"]') === null;
      openerRef.current = isReturnTarget(document.activeElement) ? document.activeElement : null;
      returnFocusRef.current = openerRef.current;
      const onFocusIn = (event: FocusEvent) => {
        if (isReturnTarget(event.target)) returnFocusRef.current = event.target;
      };
      document.addEventListener('focusin', onFocusIn);
      return () => document.removeEventListener('focusin', onFocusIn);
    }, [open, isDesktop]);

    const restoreFocus = React.useCallback(() => {
      const target = [returnFocusRef.current, openerRef.current].find(el => el?.isConnected);
      target?.focus();
    }, []);

    // On the sheet the dialog's name and description are wired from BottomSheet.Title/Description.
    const TitleTag = !isDesktop && !ariaLabel ? BottomSheet.Title : 'h2';
    const SubtitleTag = isDesktop ? 'p' : BottomSheet.Description;

    const content = (
      <>
        <header className='flex shrink-0 items-start justify-between gap-md border-b border-border-base px-lg py-md'>
          <div className='flex min-w-0 flex-col gap-2xs'>
            <TitleTag
              id={titleId}
              className='text-base font-semibold text-foreground-base break-words'
            >
              {title}
            </TitleTag>
            {subtitle && (
              <SubtitleTag id={subtitleId} className='text-sm text-foreground-muted break-words'>
                {subtitle}
              </SubtitleTag>
            )}
          </div>
          <Button
            variant='ghost'
            size='icon'
            icon='xmark'
            aria-label={closeLabel}
            title={closeLabel}
            onClick={onClose}
            className='shrink-0 text-foreground-muted hover:text-foreground-base'
          />
        </header>
        <div className='min-h-0 flex-1 overflow-y-auto px-lg py-md'>{children}</div>
        {footer && (
          <footer className='flex shrink-0 flex-wrap items-center justify-end gap-sm border-t border-border-base px-lg py-md'>
            {footer}
          </footer>
        )}
      </>
    );

    if (isDesktop) {
      if (!open) return null;
      return (
        <aside
          ref={setPanelRef}
          id={id}
          data-testid={getTestId({ testId, id })}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabel ? undefined : titleId}
          aria-describedby={subtitle ? subtitleId : undefined}
          onKeyDown={event => {
            if (event.key !== 'Escape' || event.defaultPrevented) return;
            event.preventDefault();
            onClose();
          }}
          className={cn(sidePanelVariants({ width }), className)}
        >
          <FocusReturn panelRef={panelRef} onRestore={restoreFocus} />
          {content}
        </aside>
      );
    }

    return (
      <BottomSheet
        ref={setPanelRef}
        open={open}
        onClose={onClose}
        aria-label={ariaLabel}
        id={id}
        testId={testId}
        className={cn('overflow-hidden bg-background-input', className)}
      >
        {content}
      </BottomSheet>
    );
  }
);

SidePanel.displayName = 'SidePanel';
